import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { RotateCcw, FileText } from 'lucide-react';

const Widerrufsbelehrung = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to German version if not already on it, but only once
  React.useEffect(() => {
    // Check if we're already on the German widerrufsbelehrung path
    if (location.pathname === '/de/widerrufsbelehrung') {
      return; // Already on the correct path, no need to redirect
    }
    
    // Only redirect if we're not on German and not already redirecting
    if (currentLanguage !== 'de' && !location.pathname.includes('/de/widerrufsbelehrung')) {
      navigate('/de/widerrufsbelehrung', { replace: true });
    }
  }, [currentLanguage, navigate, location.pathname]);

  // Don't render anything while redirecting
  if (currentLanguage !== 'de' && location.pathname !== '/de/widerrufsbelehrung') {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container-custom py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <RotateCcw className="h-12 w-12 text-primary-600" />
            </div>
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">
              Widerrufsbelehrung
            </h1>
            <p className="text-lg text-neutral-600">
              Widerrufsrecht für Verbraucher
            </p>
          </div>

          {/* Company Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
              Verkäufer
            </h2>
            <div className="space-y-2 text-neutral-700">
              <p><strong>surf & game GmbH</strong></p>
              <p>Amtsgericht Charlottenburg (Berlin) HRB 109460 B</p>
              <p>Hasenheide 115</p>
              <p>D-10967 Berlin</p>
              <p>Deutschland</p>
              <p className="mt-4">
                <strong>E-Mail:</strong> info@co2neutralpage.com
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Widerrufsrecht */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Widerrufsrecht
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von 
                  Gründen diesen Vertrag zu widerrufen.
                </p>
                <p className="mt-4">
                  Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag, an dem 
                  Sie oder ein von Ihnen benannter Dritter, der nicht der 
                  Beförderer ist, die Waren in Besitz genommen haben bzw. hat.
                </p>
                <p className="mt-4">
                  Um Ihr Widerrufsrecht auszuüben, müssen Sie uns
                </p>
                <div className="bg-neutral-50 p-4 rounded-lg mt-4">
                  <p><strong>surf & game GmbH</strong></p>
                  <p>Hasenheide 115</p>
                  <p>D-10967 Berlin</p>
                  <p>Deutschland</p>
                  <p className="mt-2">
                    <strong>E-Mail:</strong> info@co2neutralpage.com
                  </p>
                </div>
                <p className="mt-4">
                  mittels einer eindeutigen Erklärung (z.B. ein mit der Post 
                  versandter Brief, Telefax oder E-Mail) über Ihren Entschluss, 
                  diesen Vertrag zu widerrufen, informieren. Sie können dafür 
                  das beigefügte Muster-Widerrufsformular verwenden, das jedoch 
                  nicht vorgeschrieben ist.
                </p>
              </div>
            </div>

            {/* Widerrufsformular */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <FileText className="h-6 w-6 text-primary-600 mr-3" />
                Muster-Widerrufsformular
              </h2>
              <div className="bg-neutral-50 p-6 rounded-lg">
                <p className="font-semibold mb-4">(Wenn Sie den Vertrag widerrufen möchten, füllen Sie bitte dieses Formular aus und senden Sie es zurück.)</p>
                <div className="space-y-4">
                  <p>An:</p>
                  <p>surf & game GmbH</p>
                  <p>Hasenheide 115</p>
                  <p>D-10967 Berlin</p>
                  <p>Deutschland</p>
                  <p>E-Mail: info@co2neutralpage.com</p>
                  <div className="border-t pt-4">
                    <p>Hiermit widerrufe(n) ich/wir (*) den von mir/uns abgeschlossenen Vertrag über den Kauf der folgenden Waren/der Erbringung der folgenden Dienstleistung</p>
                    <p className="mt-2">Bestellt am (*)/erhalten am (*)</p>
                    <p className="mt-2">Name des/der Verbraucher(s)</p>
                    <p className="mt-2">Anschrift des/der Verbraucher(s)</p>
                    <p className="mt-2">Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier)</p>
                    <p className="mt-2">Datum</p>
                    <p className="text-sm text-neutral-600 mt-4">(*) Unzutreffendes streichen.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Folgen des Widerrufs */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Folgen des Widerrufs
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle 
                  Zahlungen, die wir von Ihnen erhalten haben, einschließlich 
                  der Lieferkosten (mit Ausnahme der zusätzlichen Kosten, 
                  die sich daraus ergeben, dass Sie eine andere Art der 
                  Lieferung als die von uns angebotene, günstigste Standardlieferung 
                  gewählt haben), unverzüglich und spätestens binnen vierzehn 
                  Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über 
                  Ihren Widerruf dieses Vertrags bei uns eingegangen ist. 
                  Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, 
                  das Sie bei der ursprünglichen Transaktion eingesetzt haben, 
                  es sei denn, mit Ihnen wurde ausdrücklich etwas anderes 
                  vereinbart; in keinem Fall werden Ihnen wegen dieser 
                  Rückzahlung Entgelte berechnet.
                </p>
                <p className="mt-4">
                  Wir können die Rückzahlung verweigern, bis wir die Waren 
                  wieder zurückerhalten haben oder bis Sie den Nachweis 
                  erbracht haben, dass Sie die Waren zurückgesandt haben, 
                  je nachdem, welches der frühere Zeitpunkt ist.
                </p>
                <p className="mt-4">
                  Sie haben die Waren unverzüglich und in jedem Fall 
                  spätestens binnen vierzehn Tagen ab dem Tag, an dem Sie 
                  uns über den Widerruf dieses Vertrags unterrichten, an 
                  uns zurückzusenden oder zu übergeben. Die Frist ist 
                  gewahrt, wenn Sie die Waren vor Ablauf der Frist von 
                  vierzehn Tagen absenden.
                </p>
              </div>
            </div>

            {/* Ausschluss des Widerrufsrechts */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Ausschluss des Widerrufsrechts
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Das Widerrufsrecht besteht nicht bei Verträgen
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>zur Lieferung von Waren, die nicht vorgefertigt sind und für deren Herstellung eine individuelle Auswahl oder Bestimmung durch den Verbraucher maßgeblich ist oder die eindeutig auf die persönlichen Bedürfnisse des Verbrauchers zugeschnitten sind</li>
                  <li>zur Lieferung von Waren, die schnell verderben können oder deren Verfallsdatum schnell überschritten würde</li>
                  <li>zur Lieferung versiegelter Waren, die aus Gründen des Gesundheitsschutzes oder der Hygiene nicht zur Rückgabe geeignet sind, wenn ihre Versiegelung nach der Lieferung entfernt wurde</li>
                  <li>zur Lieferung von Waren, wenn diese nach der Lieferung aufgrund ihrer Beschaffenheit untrennbar mit anderen Gütern vermischt wurden</li>
                  <li>zur Lieferung alkoholischer Getränke, deren Preis bei Abschluss des Vertrags vereinbart wurde, die aber frühestens 30 Tage nach Abschluss des Vertrags geliefert werden können und deren aktueller Wert von Schwankungen auf dem Markt abhängt, auf die der Unternehmer keinen Einfluss hat</li>
                  <li>zur Lieferung von Zeitungen, Zeitschriften oder Illustrierten mit Ausnahme von Abonnement-Verträgen</li>
                  <li>die in einer öffentlichen Versteigerung geschlossen werden</li>
                  <li>zur Lieferung von Waren, die ausdrücklich nicht für die Rückgabe geeignet sind und aus Gründen des Gesundheitsschutzes oder der Hygiene versiegelt sind, wenn ihre Versiegelung nach der Lieferung entfernt wurde</li>
                </ul>
              </div>
            </div>

            {/* Ende der Widerrufsbelehrung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Ende der Widerrufsbelehrung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Diese Widerrufsbelehrung gilt für alle von uns angebotenen 
                  Dienstleistungen, insbesondere für CO₂-Neutralitäts-Zertifizierungen 
                  und verwandte Dienstleistungen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Widerrufsbelehrung; 